import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { getFeaturedPodcasts } from '../../constants/FeaturedConstants';
import './FeaturedSection.css';

// ============================================================================
// Utility Functions
// ============================================================================

const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const formatLikes = (number) => {
  if (number >= 1_000_000) return `${(number / 1_000_000).toFixed(1)}M`;
  if (number >= 1_000) return `${(number / 1_000).toFixed(1)}K`;
  return String(number);
};

// ============================================================================
// PodcastCard Component
// ============================================================================

const PodcastCard = React.memo(({ podcast, isSelected, onClick }) => {
  const handleClick = useCallback(() => {
    onClick(podcast.id);
  }, [onClick, podcast.id]);

  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick();
      }
    },
    [handleClick]
  );

  return (
    <div
      className={`podcast-card ${isSelected ? 'podcast-card-selected' : ''}`}
      onClick={handleClick}
      onKeyPress={handleKeyPress}
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
      aria-label={`${podcast.title} by ${podcast.publishedBy}`}
    >
      <div className="podcast-card-image-wrapper">
        <img
          src={podcast.imageUrl}
          alt={podcast.title}
          className="podcast-card-image"
          loading="lazy"
        />
        <div className="podcast-card-overlay" />
        <div className="podcast-card-play-badge" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      </div>
      <div className="podcast-card-info">
        <h3 className="podcast-card-title">{podcast.title}</h3>
        <p className="podcast-card-publisher">@{podcast.publishedBy}</p>
        <div className="podcast-card-meta">
          <span className="podcast-card-duration">
            {formatDuration(podcast.duration)}
          </span>
          <span className="podcast-card-dot" aria-hidden="true">
            •
          </span>
          <div className="podcast-card-likes">
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
            <span>{formatLikes(podcast.likes)}</span>
          </div>
        </div>
      </div>
    </div>
  );
});

PodcastCard.propTypes = {
  podcast: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    publishedBy: PropTypes.string.isRequired,
    imageUrl: PropTypes.string.isRequired,
    duration: PropTypes.number.isRequired,
    likes: PropTypes.number.isRequired,
  }).isRequired,
  isSelected: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

PodcastCard.displayName = 'PodcastCard';

// ============================================================================
// AudioPlayer Component (simplified, overlayed on image)
// ============================================================================

const AudioPlayer = React.memo(({ podcast, isPlaying, onPlayPause, onEnded }) => {
  const audioRef = useRef(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      const playPromise = audio.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.catch((error) => {
          console.error('Audio playback error:', error);
        });
      }
    } else {
      audio.pause();
    }
  }, [isPlaying]);

  const handleClick = useCallback(() => {
    onPlayPause();
  }, [onPlayPause]);

  const handleEnded = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = 0;
    }
    onEnded();
  }, [onEnded]);

  return (
    <div className="audio-player">
      <audio
        ref={audioRef}
        src={podcast.audioUrl}
        preload="metadata"
        onEnded={handleEnded}
      />
      <button
        className="audio-player-play-btn"
        onClick={handleClick}
        aria-label={isPlaying ? 'Pause' : 'Play'}
        type="button"
      >
        {isPlaying ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="white" aria-hidden="true">
            <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="white" aria-hidden="true">
            <path d="M8 5v14l11-7z" />
          </svg>
        )}
      </button>
    </div>
  );
});

AudioPlayer.propTypes = {
  podcast: PropTypes.shape({
    audioUrl: PropTypes.string.isRequired,
  }).isRequired,
  isPlaying: PropTypes.bool.isRequired,
  onPlayPause: PropTypes.func.isRequired,
  onEnded: PropTypes.func.isRequired,
};

AudioPlayer.displayName = 'AudioPlayer';

// ============================================================================
// FeaturedSection Component
// ============================================================================

const FeaturedSection = () => {
  const [podcasts] = useState(getFeaturedPodcasts);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const selectedPodcast = useMemo(
    () => podcasts[selectedIndex],
    [podcasts, selectedIndex]
  );

  const handlePrevious = useCallback(() => {
    setIsPlaying(false);
    setSelectedIndex((prev) => (prev > 0 ? prev - 1 : podcasts.length - 1));
  }, [podcasts.length]);

  const handleNext = useCallback(() => {
    setIsPlaying(false);
    setSelectedIndex((prev) => (prev < podcasts.length - 1 ? prev + 1 : 0));
  }, [podcasts.length]);

  const handleCardClick = useCallback(
    (podcastId) => {
      const index = podcasts.findIndex((p) => p.id === podcastId);
      if (index !== -1 && index !== selectedIndex) {
        setIsPlaying(false);
        setSelectedIndex(index);
      }
    },
    [podcasts, selectedIndex]
  );

  const handlePlayPause = useCallback(() => {
    setIsPlaying((prev) => !prev);
  }, []);

  const handleAudioEnded = useCallback(() => {
    setIsPlaying(false);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          handlePrevious();
          break;
        case 'ArrowRight':
          e.preventDefault();
          handleNext();
          break;
        case ' ':
          e.preventDefault();
          handlePlayPause();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handlePrevious, handleNext, handlePlayPause]);

  return (
    <section id="featured-section" className="featured-section">
      <div className="featured-container">
        {/* Left Side - Scrollable Podcast List */}
        <div className="featured-left">
          <div className="featured-header">
            <h2 className="featured-title">
              <span className="featured-title-gradient">FEATURED</span> PODCASTS
            </h2>
          </div>
          <div className="featured-list-wrapper">
            <div className="featured-list">
              {podcasts.map((podcast, index) => (
                <PodcastCard
                  key={podcast.id}
                  podcast={podcast}
                  isSelected={index === selectedIndex}
                  onClick={handleCardClick}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Right Side - Detailed View */}
        <div className="featured-right">
          <div className="featured-detail-card">
            <div className="featured-detail-image-wrapper">
              <img
                src={selectedPodcast.imageUrl}
                alt={selectedPodcast.title}
                className="featured-detail-image"
              />
              <div className="featured-detail-gradient" />

              {/* Play button overlay on top of the image */}
              <AudioPlayer
                podcast={selectedPodcast}
                isPlaying={isPlaying}
                onPlayPause={handlePlayPause}
                onEnded={handleAudioEnded}
              />
            </div>

            <div className="featured-detail-content">
              <h3 className="featured-detail-title">{selectedPodcast.title}</h3>

              <div className="featured-detail-meta">
                <div className="featured-detail-publisher">
                  <div
                    className="featured-detail-avatar"
                    aria-label={`${selectedPodcast.publishedBy}'s avatar`}
                  >
                    {selectedPodcast.publishedBy.charAt(0).toUpperCase()}
                  </div>
                  <span>@{selectedPodcast.publishedBy}</span>
                </div>
                <div className="featured-detail-stats">
                  <div className="featured-detail-stat">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                    </svg>
                    <span>{formatLikes(selectedPodcast.likes)}</span>
                  </div>
                  <span className="featured-detail-dot" aria-hidden="true">
                    •
                  </span>
                  <span className="featured-detail-duration">
                    {formatDuration(selectedPodcast.duration)}
                  </span>
                </div>
              </div>

              <p className="featured-detail-description">
                {selectedPodcast.description}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedSection;
